/**
 * Export Renderer - Renders framed artwork to canvas for export
 */

import type { FramedArtwork, ProjectScene, ImageTransform } from '@frameit/types';
import { buildCanvasFilter } from '../editor/utils/color-filters';
import { getMatColor } from '../../data/mats';
import { PAPER_PRESETS } from '../../data/papers';
import { SCENE_BACKGROUNDS } from '../../data/scenes';
import { renderFrame } from '../editor/renderers/frame-renderer';

export interface ExportOptions {
  format: 'png' | 'jpg' | 'webp';
  quality: number; // 0-1
  maxDimension: number;
  watermark: boolean;
}

export interface ExportProgress {
  step: 'preparing' | 'rendering' | 'encoding' | 'finalizing';
  progress: number; // 0-1
}

/**
 * Load an image from URL
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * Apply color adjustments to CSS filter string
 */
function applyColorAdjustments(adjustments: {
  hue?: number;
  saturation?: number;
  lightness?: number;
}): string {
  const hue = adjustments.hue ?? 0;
  const saturation = adjustments.saturation ?? 0;
  const lightness = adjustments.lightness ?? 0;
  return `hue-rotate(${hue}deg) saturate(${100 + saturation}%) brightness(${100 + lightness}%)`;
}

/**
 * Draw paper texture overlay
 */
function drawPaperTexture(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  paperPreset: { color: string; texture: string },
  intensity: number,
) {
  if (paperPreset.texture === 'none') return;

  // Apply color tint
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = intensity * 0.3;
  ctx.fillStyle = paperPreset.color;
  ctx.fillRect(x, y, width, height);
  ctx.restore();

  // Add noise texture
  ctx.save();
  ctx.globalCompositeOperation = 'overlay';
  ctx.globalAlpha = intensity * 0.2;

  // Create noise pattern
  const noiseCanvas = document.createElement('canvas');
  noiseCanvas.width = 100;
  noiseCanvas.height = 100;
  const noiseCtx = noiseCanvas.getContext('2d')!;
  const imageData = noiseCtx.createImageData(100, 100);

  for (let i = 0; i < imageData.data.length; i += 4) {
    const value = Math.random() * 255;
    imageData.data[i] = value;
    imageData.data[i + 1] = value;
    imageData.data[i + 2] = value;
    imageData.data[i + 3] = 50;
  }
  noiseCtx.putImageData(imageData, 0, 0);

  // Tile the noise
  const pattern = ctx.createPattern(noiseCanvas, 'repeat');
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(x, y, width, height);
  }
  ctx.restore();
}

/**
 * Draw watermark
 */
function drawWatermark(ctx: CanvasRenderingContext2D, width: number, height: number) {
  ctx.save();
  ctx.globalAlpha = 0.3;
  ctx.fillStyle = '#ffffff';
  ctx.font = `${Math.max(16, width / 30)}px sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';

  // Add shadow for visibility
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  ctx.fillText('FrameIt', width - 20, height - 20);
  ctx.restore();
}

/**
 * Apply image transforms (crop, rotate, flip) to produce a new canvas
 */
function applyImageTransform(
  img: HTMLImageElement,
  transform: ImageTransform | undefined,
): { canvas: HTMLCanvasElement; width: number; height: number } {
  let srcX = 0;
  let srcY = 0;
  let srcW = img.width;
  let srcH = img.height;

  // Apply crop
  if (transform?.cropRect) {
    const c = transform.cropRect;
    srcX = Math.round(c.x * img.width);
    srcY = Math.round(c.y * img.height);
    srcW = Math.round(c.width * img.width);
    srcH = Math.round(c.height * img.height);
  }

  const rotation = transform?.rotation ?? 0;
  const flipH = transform?.flipH ?? false;
  const flipV = transform?.flipV ?? false;

  // Final dimensions after rotation
  const swapDims = rotation === 90 || rotation === 270;
  const outW = swapDims ? srcH : srcW;
  const outH = swapDims ? srcW : srcH;

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d')!;

  ctx.save();

  // Move origin to center for rotation/flip
  ctx.translate(outW / 2, outH / 2);

  // Apply rotation
  if (rotation !== 0) {
    ctx.rotate((rotation * Math.PI) / 180);
  }

  // Apply flip
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

  // Draw the cropped source centered at origin
  ctx.drawImage(img, srcX, srcY, srcW, srcH, -srcW / 2, -srcH / 2, srcW, srcH);

  ctx.restore();

  return { canvas, width: outW, height: outH };
}

/**
 * Render framed artwork to canvas
 */
export async function renderExport(
  artwork: FramedArtwork,
  artworkImageUrl: string,
  scene: ProjectScene | undefined,
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void,
): Promise<Blob> {
  onProgress?.({ step: 'preparing', progress: 0 });

  // Load artwork image and apply transforms (crop/rotate/flip)
  const rawImage = await loadImage(artworkImageUrl);
  const {
    canvas: transformedCanvas,
    width: txWidth,
    height: txHeight,
  } = applyImageTransform(rawImage, artwork.imageTransform);
  onProgress?.({ step: 'preparing', progress: 0.3 });

  // Calculate dimensions using transformed image
  const displayScale = artwork.displayScale ?? 1;
  const imgWidth = txWidth;
  const imgHeight = txHeight;

  // Frame and mat dimensions
  const frameWidth = artwork.frame.width ?? 25;
  const matWidth = artwork.mat ? (artwork.mat.width ?? 0.1) * Math.min(imgWidth, imgHeight) : 0;

  // Total framed dimensions (in image pixels)
  const framedWidth = imgWidth + frameWidth * 2 + matWidth * 2;
  const framedHeight = imgHeight + frameWidth * 2 + matWidth * 2;

  // Scale to max dimension
  let canvasWidth: number;
  let canvasHeight: number;

  if (scene) {
    // Scene mode: use 16:9 aspect ratio
    canvasWidth = options.maxDimension;
    canvasHeight = Math.round((options.maxDimension * 9) / 16);
  } else {
    // Artwork only mode
    const scale =
      Math.min(options.maxDimension / framedWidth, options.maxDimension / framedHeight) *
      displayScale;

    canvasWidth = Math.round(framedWidth * scale);
    canvasHeight = Math.round(framedHeight * scale);
  }

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d')!;

  onProgress?.({ step: 'rendering', progress: 0 });

  // Draw scene background if present
  if (scene) {
    const bgUrl = SCENE_BACKGROUNDS[scene.templateId];
    if (bgUrl) {
      try {
        const bgImage = await loadImage(bgUrl);
        // Draw background covering canvas
        const bgAspect = bgImage.width / bgImage.height;
        const canvasAspect = canvasWidth / canvasHeight;

        let drawWidth: number;
        let drawHeight: number;
        let drawX: number;
        let drawY: number;

        if (bgAspect > canvasAspect) {
          drawHeight = canvasHeight;
          drawWidth = canvasHeight * bgAspect;
          drawX = (canvasWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = canvasWidth;
          drawHeight = canvasWidth / bgAspect;
          drawX = 0;
          drawY = (canvasHeight - drawHeight) / 2;
        }

        ctx.drawImage(bgImage, drawX, drawY, drawWidth, drawHeight);
      } catch {
        // Fallback to solid color
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }
    }
  } else {
    // Neutral background for artwork-only export
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }

  onProgress?.({ step: 'rendering', progress: 0.3 });

  // Get scene placement for this artwork (if in scene mode)
  const placement = scene?.placements?.find((p) => p.framedArtworkId === artwork.id);

  // Calculate framed artwork position
  // EditorCanvas uses: baseSize (300px) * sceneScale * displayScale
  // For a typical editor canvas of ~800px, this means scale 0.4 gives ~15% of canvas width
  // We convert the placement scale to a proportional size: (300 * scale) / 800 ≈ 0.375 * scale
  // Increased multiplier slightly (0.45) for better visual match
  const placementScale = placement?.scale ?? 0.4;
  const proportionalScale = 0.45 * placementScale;

  const scaledFramedWidth = scene ? canvasWidth * proportionalScale * displayScale : canvasWidth;
  const scaledFramedHeight = scene
    ? scaledFramedWidth / (framedWidth / framedHeight)
    : canvasHeight;

  // Use placement offset if available (offset from center, where 0,0 is center)
  // EditorCanvas uses vw/vh units: translateY = offset * 50vh
  // For export, we scale this proportionally to canvas dimensions
  const offsetX = placement?.offset?.x ?? 0;
  const offsetY = placement?.offset?.y ?? 0;

  // Calculate position - center plus offset
  // In EditorCanvas, offset * 50vw/vh creates the translation
  // We use a similar proportional approach for export
  const frameX = scene
    ? canvasWidth / 2 - scaledFramedWidth / 2 + offsetX * canvasWidth * 0.5
    : (canvasWidth - scaledFramedWidth) / 2;

  // For Y position, default to upper-middle portion of canvas (40% from top) when offset is 0
  // This matches typical wall art placement in interior scenes
  const baseY = scene ? canvasHeight * 0.4 : canvasHeight / 2;
  const frameY = scene
    ? baseY - scaledFramedHeight / 2 + offsetY * canvasHeight * 0.5
    : (canvasHeight - scaledFramedHeight) / 2;

  const scaleFactor = scaledFramedWidth / framedWidth;
  const scaledFrameWidth = frameWidth * scaleFactor;
  const scaledMatWidth = matWidth * scaleFactor;

  // Draw shadow
  const shadow = artwork.shadow;
  if (shadow?.enabled !== false) {
    const shadowBlur = (shadow?.blur ?? 15) * scaleFactor;
    const shadowOffsetX = (shadow?.offsetX ?? 5) * scaleFactor;
    const shadowOffsetY = (shadow?.offsetY ?? 5) * scaleFactor;
    const shadowOpacity = shadow?.opacity ?? 0.3;
    const shadowColor = shadow?.color ?? '#000000';

    // Parse shadow color hex to rgb
    const sNum = parseInt(shadowColor.slice(1), 16);
    const sR = (sNum >> 16) & 255;
    const sG = (sNum >> 8) & 255;
    const sB = sNum & 255;

    ctx.save();
    ctx.shadowColor = `rgba(${sR},${sG},${sB},${shadowOpacity})`;
    ctx.shadowBlur = shadowBlur;
    ctx.shadowOffsetX = shadowOffsetX;
    ctx.shadowOffsetY = shadowOffsetY;
    ctx.fillStyle = '#000';
    ctx.fillRect(frameX, frameY, scaledFramedWidth, scaledFramedHeight);
    ctx.restore();
  }

  // Draw frame using Canvas 2D renderer
  const innerW = Math.round(scaledFramedWidth - scaledFrameWidth * 2);
  const innerH = Math.round(scaledFramedHeight - scaledFrameWidth * 2);
  const frameCanvas = renderFrame({
    templateId: artwork.frame.templateId,
    frameWidth: Math.round(scaledFrameWidth),
    innerWidth: innerW,
    innerHeight: innerH,
    highlightIntensity: artwork.frame.bevelAdjustments?.highlightIntensity,
    shadowIntensity: artwork.frame.bevelAdjustments?.shadowIntensity,
    colorAdjustments: artwork.frame.colorAdjustments,
  });
  ctx.drawImage(frameCanvas, frameX, frameY, scaledFramedWidth, scaledFramedHeight);

  onProgress?.({ step: 'rendering', progress: 0.5 });

  // Draw mat (supports single, double, triple layers)
  if (artwork.mat) {
    const matColor = getMatColor(artwork.mat.templateId);
    const matX = frameX + scaledFrameWidth;
    const matY = frameY + scaledFrameWidth;
    const matAreaWidth = scaledFramedWidth - scaledFrameWidth * 2;
    const matAreaHeight = scaledFramedHeight - scaledFrameWidth * 2;
    const layers = artwork.mat.layers ?? 1;
    const bevelColor = artwork.mat.bevelColor ?? '#e8e0d0';
    const secondColor = artwork.mat.secondLayerTemplateId
      ? getMatColor(artwork.mat.secondLayerTemplateId)
      : matColor;

    // Apply color adjustments if present
    if (artwork.mat.colorAdjustments) {
      ctx.filter = applyColorAdjustments(artwork.mat.colorAdjustments);
    }

    if (layers === 1) {
      // Single mat
      ctx.fillStyle = matColor;
      ctx.fillRect(matX, matY, matAreaWidth, matAreaHeight);

      // Inner bevel shadow for single mat
      ctx.save();
      ctx.filter = 'none';
      const bevelW = Math.max(1, scaledMatWidth * 0.04);
      // Bottom/right inner shadow
      ctx.globalAlpha = 0.15;
      ctx.fillStyle = '#000';
      ctx.fillRect(matX + scaledMatWidth - bevelW, matY, bevelW, matAreaHeight);
      ctx.fillRect(matX, matY + matAreaHeight - scaledMatWidth, matAreaWidth, bevelW);
      // Top/left inner highlight
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = '#fff';
      ctx.fillRect(matX, matY + scaledMatWidth - bevelW, matAreaWidth, bevelW);
      ctx.fillRect(matX + matAreaWidth - scaledMatWidth, matY, bevelW, matAreaHeight);
      ctx.restore();
    } else {
      // Multi-layer mat
      const layerWidth = scaledMatWidth / layers;
      const bevelW = Math.max(1, scaledMatWidth * 0.04);

      // Outer mat layer
      ctx.fillStyle = matColor;
      ctx.fillRect(matX, matY, matAreaWidth, matAreaHeight);

      // Bevel line between outer and inner
      ctx.save();
      ctx.filter = 'none';
      ctx.strokeStyle = bevelColor;
      ctx.lineWidth = bevelW;
      ctx.strokeRect(
        matX + layerWidth - bevelW / 2,
        matY + layerWidth - bevelW / 2,
        matAreaWidth - (layerWidth - bevelW / 2) * 2,
        matAreaHeight - (layerWidth - bevelW / 2) * 2,
      );
      // Bevel highlights
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = '#fff';
      ctx.fillRect(
        matX + layerWidth - bevelW,
        matY + layerWidth - bevelW,
        matAreaWidth - (layerWidth - bevelW) * 2,
        1,
      );
      ctx.fillRect(
        matX + layerWidth - bevelW,
        matY + layerWidth - bevelW,
        1,
        matAreaHeight - (layerWidth - bevelW) * 2,
      );
      ctx.restore();

      // Inner mat layer (second color)
      if (artwork.mat.colorAdjustments) {
        ctx.filter = applyColorAdjustments(artwork.mat.colorAdjustments);
      }
      ctx.fillStyle = secondColor;
      ctx.fillRect(
        matX + layerWidth,
        matY + layerWidth,
        matAreaWidth - layerWidth * 2,
        matAreaHeight - layerWidth * 2,
      );

      // Third layer
      if (layers >= 3) {
        // Bevel line between second and third
        ctx.save();
        ctx.filter = 'none';
        ctx.strokeStyle = bevelColor;
        ctx.lineWidth = bevelW;
        ctx.strokeRect(
          matX + layerWidth * 2 - bevelW / 2,
          matY + layerWidth * 2 - bevelW / 2,
          matAreaWidth - (layerWidth * 2 - bevelW / 2) * 2,
          matAreaHeight - (layerWidth * 2 - bevelW / 2) * 2,
        );
        ctx.restore();

        // Third mat layer
        if (artwork.mat.colorAdjustments) {
          ctx.filter = applyColorAdjustments(artwork.mat.colorAdjustments);
        }
        ctx.fillStyle = matColor;
        ctx.fillRect(
          matX + layerWidth * 2,
          matY + layerWidth * 2,
          matAreaWidth - layerWidth * 4,
          matAreaHeight - layerWidth * 4,
        );
      }
    }

    ctx.filter = 'none';
  }

  onProgress?.({ step: 'rendering', progress: 0.7 });

  // Draw artwork image
  const artX = frameX + scaledFrameWidth + scaledMatWidth;
  const artY = frameY + scaledFrameWidth + scaledMatWidth;
  const artWidth = scaledFramedWidth - (scaledFrameWidth + scaledMatWidth) * 2;
  const artHeight = scaledFramedHeight - (scaledFrameWidth + scaledMatWidth) * 2;

  // Apply color grading filters (draw the transformed canvas, not the raw image)
  const colorFilter = buildCanvasFilter(artwork.colorGrading);
  if (colorFilter !== 'none') {
    ctx.save();
    ctx.filter = colorFilter;
    ctx.drawImage(transformedCanvas, artX, artY, artWidth, artHeight);
    ctx.restore();
  } else {
    ctx.drawImage(transformedCanvas, artX, artY, artWidth, artHeight);
  }

  // Apply paper texture
  if (artwork.paper) {
    const preset = PAPER_PRESETS[artwork.paper.templateId];
    if (preset) {
      drawPaperTexture(
        ctx,
        artX,
        artY,
        artWidth,
        artHeight,
        preset,
        artwork.paper.textureIntensity ?? 0.5,
      );
    }
  }

  onProgress?.({ step: 'rendering', progress: 1 });
  onProgress?.({ step: 'encoding', progress: 0 });

  // Draw watermark if required
  if (options.watermark) {
    drawWatermark(ctx, canvasWidth, canvasHeight);
  }

  onProgress?.({ step: 'encoding', progress: 0.5 });

  // Convert to blob
  const mimeType = options.format === 'jpg' ? 'image/jpeg' : `image/${options.format}`;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          onProgress?.({ step: 'finalizing', progress: 1 });
          resolve(blob);
        } else {
          reject(new Error('Failed to create export blob'));
        }
      },
      mimeType,
      options.quality,
    );
  });
}
